import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, useApi } from "@/lib/api";

const splitList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export function CourseForm({ initial, onSubmit, submitLabel = "Save" }) {
  const api = useApi();
  const [currencies, setCurrencies] = useState([]);
  const [f, setF] = useState({
    title: initial?.title || "", description: initial?.description || "", level: initial?.level || "beginner",
    duration_hours: initial?.duration_hours ?? 2, skills: (initial?.skills || []).join(", "),
    prerequisites: (initial?.prerequisites || []).join(", "), price: initial?.price ?? 0,
    currency: initial?.currency || "", cover_url: initial?.cover_url || "",
    category: initial?.category || "", tags: (initial?.tags || []).join(", "),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/settings").then((r) => {
      setCurrencies(r.data.currencies || []);
      setF((p) => ({ ...p, currency: p.currency || r.data.default_currency }));
    });
  }, [api]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit({
        ...f, duration_hours: Number(f.duration_hours), price: Number(f.price) || 0,
        skills: splitList(f.skills), prerequisites: splitList(f.prerequisites), tags: splitList(f.tags), cover_url: f.cover_url.trim() || null,
      });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="course-form">
      <div className="space-y-1.5"><Label>Title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} required minLength={3} maxLength={140} data-testid="course-title-input" /></div>
      <div className="space-y-1.5"><Label>Description</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} rows={4} maxLength={4000} data-testid="course-description-input" /></div>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label>Level</Label>
          <Select value={f.level} onValueChange={(v) => set("level", v)}>
            <SelectTrigger data-testid="course-level-select"><SelectValue /></SelectTrigger>
            <SelectContent>{["beginner", "intermediate", "advanced"].map((l) => <SelectItem key={l} value={l} className="capitalize">{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Duration (hours)</Label><Input type="number" min={0.5} step={0.5} value={f.duration_hours} onChange={(e) => set("duration_hours", e.target.value)} required data-testid="course-duration-input" /></div>
        <div className="space-y-1.5"><Label>Cover image URL</Label><Input value={f.cover_url} onChange={(e) => set("cover_url", e.target.value)} placeholder="https://" data-testid="course-cover-input" /></div>
      </div>
      <div className="space-y-1.5"><Label>Skills taught (comma separated)</Label><Input value={f.skills} onChange={(e) => set("skills", e.target.value)} data-testid="course-skills-input" /></div>
      <div className="space-y-1.5"><Label>Prerequisites (comma separated)</Label><Input value={f.prerequisites} onChange={(e) => set("prerequisites", e.target.value)} data-testid="course-prereq-input" /></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label htmlFor="cf-cat">Category</Label><Input id="cf-cat" value={f.category} onChange={(e) => set("category", e.target.value)} maxLength={60} data-testid="course-category-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="cf-tags">Tags (comma separated)</Label><Input id="cf-tags" value={f.tags} onChange={(e) => set("tags", e.target.value)} data-testid="course-tags-input" /></div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Price (0 = free)</Label><Input type="number" min={0} value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="course-price-input" /></div>
        <div className="space-y-1.5">
          <Label>Currency</Label>
          <Select value={f.currency} onValueChange={(v) => set("currency", v)}>
            <SelectTrigger data-testid="course-currency-select"><SelectValue placeholder="Currency" /></SelectTrigger>
            <SelectContent>{currencies.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`course-currency-${c.code}`}>{c.symbol} {c.code} — {c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Button type="submit" disabled={busy} className="rounded-full bg-[#222d99] hover:bg-[#1a237e]" data-testid="course-form-submit">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {submitLabel}
      </Button>
    </form>
  );
}
